import { Helmet } from "react-helmet-async";

/**
 * Site-wide JSON-LD blob rendered on EVERY page.
 *
 * Designed for both classical SEO (Google / Bing / Yandex) and modern AI
 * answer engines (Google AI Overview, ChatGPT Search, Gemini, Perplexity,
 * Claude, Copilot). The graph deliberately includes:
 *
 *  • Organization + EducationalOrganization (one @id, two types) — strongest
 *    Knowledge Graph entity signal.
 *  • LocalBusiness nodes for each branch (Jabalpur / Bhopal / Nagpur) with
 *    geo coordinates so Google Maps + AI assistants can answer
 *    "home tutor near me" queries.
 *  • knowsAbout — explicit entity coverage (subjects, boards, exams) so LLMs
 *    treat Anand Tutorials as an authoritative source on each topic.
 *  • hasOfferCatalog — itemised list of services so AI summaries can pull
 *    structured offers.
 *  • Speakable — flags the Quick-Summary + FAQ regions for voice-assistant
 *    extraction (Google Assistant, Siri, Alexa).
 *  • WebSite with SearchAction — the standard sitelinks search-box hint.
 *  • SiteNavigationElement — primary nav surfaced as a separate node.
 *  • ContactPoint (customer service + sales).
 */

const SITE_URL = "https://anandtutorials.com";
const LOGO_URL = "https://anandtutorials.com/logo-anand-tutorials.png";

const HEAD_OFFICE_MAP_URL = "https://maps.app.goo.gl/86xQV7m8AvTJ42XPA?g_st=ac";
const BHOPAL_MAP_URL = "https://www.google.com/maps/search/?api=1&query=Anand+Tutorials+Near+South+T.T+Nagar+Aradhana+Nagar+Dr+Ambedkar+Nagar+Bhopal+462003";
const NAGPUR_MAP_URL = "https://www.google.com/maps/search/?api=1&query=Anand+Tutorials+Borgaon+Road+Patel+Nagar+Manjidana+Colony+Nagpur+440013";

const BRANCHES = [
    {
        id: `${SITE_URL}/#branch-jabalpur`,
        name: "Anand Tutorials — Jabalpur (Head Office)",
        street: "Ghadi Chowk, Vijay Nagar",
        city: "Jabalpur", state: "Madhya Pradesh", pin: "482002",
        phone: "+91 7974221232",
        lat: 23.1815, lng: 79.9864,
        url: `${SITE_URL}/home-tuition-jabalpur`,
        hasMap: HEAD_OFFICE_MAP_URL,
        isHeadOffice: true,
    },
    {
        id: `${SITE_URL}/#branch-bhopal`,
        name: "Anand Tutorials — Bhopal",
        street: "Near South T.T. Nagar, Dr. Ambedkar Nagar, Aradhana Nagar",
        city: "Bhopal", state: "Madhya Pradesh", pin: "462003",
        phone: "+91 7974221232",
        lat: 23.2247, lng: 77.4207,
        url: `${SITE_URL}/home-tuition-bhopal`,
        hasMap: BHOPAL_MAP_URL,
    },
    {
        id: `${SITE_URL}/#branch-nagpur`,
        name: "Anand Tutorials — Nagpur",
        street: "Borgaon Road, Patel Nagar, Manjidana Colony",
        city: "Nagpur", state: "Maharashtra", pin: "440013",
        phone: "+91 7974221232",
        lat: 21.1458, lng: 79.0882,
        url: `${SITE_URL}/home-tuition-nagpur`,
        hasMap: NAGPUR_MAP_URL,
    },
];

const KNOWS_ABOUT = [
    // Subjects
    "Mathematics", "Physics", "Chemistry", "Biology", "English Language",
    "English Literature", "Hindi", "Sanskrit", "Social Science",
    "Computer Science", "Java Programming", "Python Programming", "C Programming",
    "C++ Programming", "HTML", "Accountancy", "Economics", "Business Studies",
    "Commerce", "Spoken English", "Coding for Kids", "Olympiad Preparation",
    // Boards
    "CBSE", "ICSE", "ISC", "MP Board", "Maharashtra Board", "IB", "IGCSE",
    "Cambridge", "IB Diploma", "International Curriculum",
    // Exams
    "NEET", "IIT JEE", "JEE Main", "JEE Advanced", "CUET",
    "Navodaya Entrance", "Sainik School Entrance", "NTSE", "KVPY",
    "Olympiads", "Government Exams", "Competitive Exams",
    // Modalities + cohorts
    "Home Tuition", "Personal Tutor", "Private Tutor", "1:1 Tutoring",
    "Online Tuition", "Live Online Classes", "NRI Education",
    "Foreign Student Tutoring", "Personalized Learning",
];

const SERVICE_CATALOG_ITEMS = [
    { name: "Home Tuition (Jabalpur, Bhopal, Nagpur)", desc: "1:1 in-person personal home tutors verified by Anand Tutorials." },
    { name: "Online Tuition — India", desc: "Live 1:1 personal tutoring via Zoom / Google Meet, all boards & classes." },
    { name: "Online Tuition — International (USA, UK, Canada, Australia, UAE, Singapore)", desc: "Indian curriculum & global boards taught live by senior Indian teachers." },
    { name: "CBSE / ICSE / State Board Coaching", desc: "Board-aligned 1:1 tutoring for Class 1 to Class 12 with regular tests." },
    { name: "NEET-UG Home Coaching", desc: "Personal 1:1 NEET tutors covering Physics, Chemistry, Biology with PYQ analysis." },
    { name: "IIT JEE Home Coaching", desc: "JEE Main + Advanced 1:1 mentoring with concept-first methodology." },
    { name: "Spoken English & Communication Skills", desc: "Confidence-building spoken English for students and professionals." },
    { name: "Coding & Computer Science", desc: "Java, Python, C, C++, HTML for school students and degree learners." },
    { name: "Olympiad Preparation", desc: "Maths + Science Olympiad coaching for Class 3 to Class 10." },
];

export default function SiteSchema() {
    const organization = {
        "@context": "https://schema.org",
        "@type": ["EducationalOrganization", "Organization", "LocalBusiness"],
        "@id": `${SITE_URL}/#organization`,
        "name": "Anand Tutorials",
        "alternateName": [
            "Anand Home Tutorials", "Anand Tutorials Online",
            "Anand Tutorials Jabalpur", "Anand Tutorials Bhopal", "Anand Tutorials Nagpur",
        ],
        "url": SITE_URL,
        "logo": { "@type": "ImageObject", "url": LOGO_URL, "width": 512, "height": 512 },
        "image": LOGO_URL,
        "description":
            "Anand Tutorials is India's most trusted 1-on-1 home tuition and live online tutoring brand. " +
            "12+ years of experience, 50,000+ students across 27 countries, 2,500+ verified tutors. " +
            "Home tuition branches in Jabalpur, Bhopal and Nagpur. " +
            "Live online classes worldwide for CBSE, ICSE, MP Board, Maharashtra Board, IB, IGCSE, " +
            "Cambridge, NEET, IIT JEE, CUET, Olympiad and competitive exams.",
        "slogan": "Personal Teacher. Personal Attention. Personal Growth.",
        "foundingDate": "2013",
        "founder": { "@type": "Person", "name": "Anand Rajput" },
        "telephone": "+91-7000530316",
        "email": "anandhometutorials@gmail.com",
        "priceRange": "₹₹",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Ghadi Chowk, Vijay Nagar",
            "addressLocality": "Jabalpur",
            "addressRegion": "Madhya Pradesh",
            "postalCode": "482002",
            "addressCountry": "IN",
        },
        "hasMap": HEAD_OFFICE_MAP_URL,
        "geo": { "@type": "GeoCoordinates", "latitude": 23.1815, "longitude": 79.9864 },
        "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "Country", "name": "Canada" },
            { "@type": "Country", "name": "Australia" },
            { "@type": "Country", "name": "United Arab Emirates" },
            { "@type": "Country", "name": "Singapore" },
            { "@type": "Country", "name": "Saudi Arabia" },
            { "@type": "Country", "name": "Qatar" },
            { "@type": "Country", "name": "Oman" },
            { "@type": "Country", "name": "Worldwide" },
        ],
        "knowsAbout": KNOWS_ABOUT,
        "knowsLanguage": ["en", "hi"],
        "sameAs": [
            "https://www.instagram.com/anandtutorialsofficial",
            "https://www.facebook.com/anandtutorialsofficial",
            "https://www.youtube.com/@anandtutorials",
            "https://www.linkedin.com/company/anand-tutorials",
            "https://anandtutorials.com",
        ],
        "department": BRANCHES.map((b) => ({ "@id": b.id })),
        "contactPoint": [
            {
                "@type": "ContactPoint",
                "telephone": "+91-7000530316",
                "contactType": "customer service",
                "areaServed": ["IN", "Worldwide"],
                "availableLanguage": ["en", "hi"],
                "contactOption": ["TollFree", "HearingImpairedSupported"],
            },
            {
                "@type": "ContactPoint",
                "telephone": "+91-7000530316",
                "contactType": "sales",
                "areaServed": "Worldwide",
                "availableLanguage": ["en", "hi"],
            },
        ],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "1200",
            "bestRating": "5", "worstRating": "1",
        },
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Anand Tutorials — Service Catalog",
            "itemListElement": SERVICE_CATALOG_ITEMS.map((s, i) => ({
                "@type": "Offer",
                "position": i + 1,
                "itemOffered": {
                    "@type": "Service",
                    "name": s.name,
                    "description": s.desc,
                    "provider": { "@id": `${SITE_URL}/#organization` },
                    "areaServed": "Worldwide",
                },
            })),
        },
        "award": [
            "Top Home Tuition Brand — Jabalpur (Customer Choice 2024)",
            "Featured Personalised 1:1 Education Platform (12+ years)",
        ],
        "memberOf": { "@type": "Organization", "name": "Education Network India" },
    };

    const branchNodes = BRANCHES.map((b) => ({
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "EducationalOrganization"],
        "@id": b.id,
        "name": b.name,
        "url": b.url,
        "telephone": b.phone,
        "image": LOGO_URL,
        "parentOrganization": { "@id": `${SITE_URL}/#organization` },
        "address": {
            "@type": "PostalAddress",
            "streetAddress": b.street,
            "addressLocality": b.city,
            "addressRegion": b.state,
            "postalCode": b.pin,
            "addressCountry": "IN",
        },
        // hasMap on the Head Office branch node only — branch offices keep
        // their own local map surfaces (Google Business Profile links etc).
        ...(b.hasMap ? { "hasMap": b.hasMap } : {}),
        "geo": { "@type": "GeoCoordinates", "latitude": b.lat, "longitude": b.lng },
        "areaServed": { "@type": "City", "name": b.city },
        "priceRange": "₹₹",
        "openingHoursSpecification": [
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "08:00", "closes": "22:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Sunday",
                "opens": "10:00", "closes": "18:00",
            },
        ],
    }));

    const website = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "url": SITE_URL,
        "name": "Anand Tutorials",
        "alternateName": "Personal 1:1 Home & Online Tuition India",
        "description":
            "Find verified 1:1 home tutors in Jabalpur, Bhopal, Nagpur and book live online personal classes anywhere in India or worldwide.",
        "publisher": { "@id": `${SITE_URL}/#organization` },
        "inLanguage": "en-IN",
        "potentialAction": [
            {
                "@type": "SearchAction",
                "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/blog?q={search_term_string}` },
                "query-input": "required name=search_term_string",
            },
            {
                "@type": "SubscribeAction",
                "target": `${SITE_URL}/register`,
                "object": { "@type": "Service", "name": "Free Demo Class" },
            },
        ],
    };

    // SiteNavigationElement — primary public nav. Helps AI assistants
    // surface the right sub-page when a user asks "go to your fee page" etc.
    const navigation = {
        "@context": "https://schema.org",
        "@type": "SiteNavigationElement",
        "name": ["Home", "How It Works", "Courses", "Online Classes", "Foreign Students", "Blog", "FAQs", "Contact", "Book Free Demo", "Become a Tutor"],
        "url": [
            `${SITE_URL}/`, `${SITE_URL}/how-it-works`, `${SITE_URL}/courses`,
            `${SITE_URL}/online-classes`, `${SITE_URL}/foreign-students`,
            `${SITE_URL}/blog`, `${SITE_URL}/faq`, `${SITE_URL}/contact`,
            `${SITE_URL}/register`, `${SITE_URL}/become-a-tutor`,
        ],
    };

    // Speakable schema — flags content regions that voice assistants and AI
    // answer engines can extract and read aloud / quote.
    const speakable = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage-speakable`,
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
                "h1",
                ".speakable",
                "[data-speakable=\"true\"]",
                ".faq-question",
                ".faq-answer",
                ".quick-summary",
            ],
            "xpath": [
                "/html/head/title",
                "/html/head/meta[@name='description']/@content",
            ],
        },
    };

    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(organization)}</script>
            {branchNodes.map((b) => (
                <script key={b["@id"]} type="application/ld+json">{JSON.stringify(b)}</script>
            ))}
            <script type="application/ld+json">{JSON.stringify(website)}</script>
            <script type="application/ld+json">{JSON.stringify(navigation)}</script>
            <script type="application/ld+json">{JSON.stringify(speakable)}</script>
        </Helmet>
    );
}
